import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useJunctionGatesLayer } from "../layers/useJunctionGatesLayer";
import { useJunctionMarkersLayer } from "../layers/useJunctionMarkersLayer";
import { useJunctionMovementSegmentsLayer } from "../layers/useJunctionMovementSegmentsLayer";
import type { DateTime } from "luxon";
import { useJunctionVehicleCounts } from "@/api/hooks/useJunctionVehicleCounts";
import { TrajectoryPointDtoVehicleType } from "@/api/generated/models/trajectoryPointDtoVehicleType";
import { buildMovementColorMap } from "@/utils/colourUtils";
interface UseJunctionAnalyticsViewParams {
  active: boolean;
  displayedVehicleTypes: TrajectoryPointDtoVehicleType[];
  selectedStartTime: DateTime | null;
  selectedEndTime: DateTime | null;
}
export function useJunctionAnalyticsView(t0) {
  const $ = _c(25);
  const {
    active,
    displayedVehicleTypes,
    selectedStartTime,
    selectedEndTime
  } = t0;
  const [selectedJunctionId, setSelectedJunctionId] = useState(null);
  const [selectedMovementId, setSelectedMovementId] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = junctionId => {
      setSelectedJunctionId(prev => prev === junctionId ? null : junctionId);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleJunctionClick = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setSelectedJunctionId(null);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const clearSelectedJunction = t2;
  let t3;
  let t4;
  if ($[2] !== active) {
    t3 = () => {
      if (!active) {
        setSelectedJunctionId(null);
      }
    };
    t4 = [active];
    $[2] = active;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setSelectedMovementId(null);
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== selectedJunctionId) {
    t6 = [selectedJunctionId];
    $[6] = selectedJunctionId;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  useEffect(t5, t6);
  const markersVisible = active && selectedJunctionId === null;
  const gatesVisible = active && selectedJunctionId !== null;
  const segmentsVisible = active && selectedJunctionId !== null;
  const markersLayer = useJunctionMarkersLayer(markersVisible, handleJunctionClick);
  const gatesLayer = useJunctionGatesLayer(selectedJunctionId, gatesVisible);
  let t7;
  if ($[8] !== selectedStartTime) {
    t7 = selectedStartTime?.toISO({
      includeOffset: false
    }) ?? null;
    $[8] = selectedStartTime;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== selectedEndTime) {
    t8 = selectedEndTime?.toISO({
      includeOffset: false
    }) ?? null;
    $[10] = selectedEndTime;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== displayedVehicleTypes || $[13] !== t7 || $[14] !== t8) {
    t9 = {
      vehicleTypes: displayedVehicleTypes,
      startTimestamp: t7,
      endTimestamp: t8
    };
    $[12] = displayedVehicleTypes;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const {
    data: vehicleCountsData
  } = useJunctionVehicleCounts(selectedJunctionId, t9);
  vehicleCountsData?.counts;
  let t10;
  if ($[16] !== vehicleCountsData?.counts) {
    t10 = buildMovementColorMap(vehicleCountsData?.counts.map(_temp) ?? []);
    $[16] = vehicleCountsData?.counts;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const colorByMovementId = t10;
  const segmentsLayer = useJunctionMovementSegmentsLayer(selectedJunctionId, displayedVehicleTypes, selectedStartTime, selectedEndTime, colorByMovementId, selectedMovementId, segmentsVisible);
  let t11;
  if ($[18] !== gatesLayer || $[19] !== markersLayer || $[20] !== segmentsLayer || $[21] !== selectedJunctionId || $[22] !== selectedMovementId || $[23] !== vehicleCountsData) {
    t11 = {
      markersLayer,
      gatesLayer,
      segmentsLayer,
      selectedJunctionId,
      selectedMovementId,
      setSelectedMovementId,
      handleJunctionClick,
      clearSelectedJunction,
      vehicleCountsData
    };
    $[18] = gatesLayer;
    $[19] = markersLayer;
    $[20] = segmentsLayer;
    $[21] = selectedJunctionId;
    $[22] = selectedMovementId;
    $[23] = vehicleCountsData;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
}
function _temp(c) {
  return c.movementId;
}